import { localStorage as safeLocalStorage, sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePhoneVerificationNavigator from 'hooks/usePhoneVerificationNavigator';
import dayjs from 'dayjs';
import { useHistory, useLocation } from 'react-router-dom';
import MonthView from '../../../components/DentistCalendar/MonthView';
import styles from './HygienistHome.module.scss';
import Api from '../../../api';
import { ChatbaseWidget, openChatbaseWidget, requestChatbaseSupport, type ChatbaseUserContext } from '../../../components/ChatWidget';
import { exitReact } from '../../../utils';
import useHygienistCalendarData from './hooks/useHygienistCalendarData';
import useHygienistSocketEvents from './hooks/useHygienistSocketEvents';
import {
  markTalentFeedbackHomePromptShown,
  shouldAutoPromptTalentFeedbackJob,
  shouldSuppressTalentFeedbackHomePrompt,
} from '../../../utils/talentFeedbackSession';
import { PARTIAL_OFFER_STATUS } from '../../../utils/constants';
import useAvailableNow from './hooks/useAvailableNow';
import { AvailableNowInfoModal, AvailableNowPenaltyModal, ShiftsTodayPromptModal } from './components/AvailableNow/AvailableNowModals'
import { JOB_STATUS } from '../../../constants/dentist/jobStatus';
import HygienistJobSelectionModal, { HygienistOfferItem } from './components/HygienistJobSelectionModal';
import InstaBookOptionsModal from './components/InstaBookOptionsModal/InstaBookOptionsModal'
import { InstaVideoModal, InstaTermsModal, InstaSuccessModal } from '../../../components/Modals/InstaBookModals'
import { EmptyDayModal, ManualShiftCongratsModal, ManualShiftInfoModal } from '../../../components/Modals/GenericModals'
import HoursPickerModal from './components/HoursPickerModal/HoursPickerModal'
import { SimpleModal } from '../../../components/Modal'
import ConfirmDialog from '../../../components/ConfirmDialog'
import BrandedAlertModal from '../../../components/Modal/BrandedAlertModal/BrandedAlertModal'
import PhotoAnnounceModal from '../../../components/PhotoAnnounceModal/PhotoAnnounceModal'
import EducationCredentialsConfirmDialog from '../../../components/EducationCredentialsConfirmDialog'
import MembershipBannerMessage from '../../../components/MembershipBannerMessage';
import TalentNavbar from '../../../components/TalentNavbar';
import { NoShiftsModal } from './components/NoShiftsModal/NoShiftsModal';
import FixedLoader from '../../../components/FixedLoader';
import type { MembershipStatusResponse } from '../../../utils/types';
import { MembershipRestrictionModal, type MembershipDialogCopy } from '../../../components/Modal';
import {
  canManageAutobookScheduleByMembership,
  canShowAutobookActivationCta,
  canUseInstabookByMembership,
  getCancellationCooldownDialogCopy,
  getMembershipBannerCopy,
  isDateBlockedByCancellationCooldown,
  openMembershipPath,
  parseBlockedUntilMs,
  shouldShowShiftVisibilityRestrictionBanner,
} from '../../../utils/membership';
import infoIcon from '../../../images/info-icon.svg';
import calendarIcon from '../../../images/calendar_icon.svg';
import WaveHandIcon from '../../../components/WaveHandIcon/WaveHandIcon';
import { blurActiveElementAndHideKeyboard } from '../../../utils/nativeApp';
import { AUTOBOOK_WINDOW_MESSAGE, isWithinAutobookWindow } from '../../../utils/autobookWindow';

export function normalizePendingTalentReviews(pending: unknown): any[] {
  if (Array.isArray(pending)) return pending;
  if (Array.isArray((pending as any)?.data)) return (pending as any).data;
  return [];
}

export function selectHomePendingTalentReview(
  pending: unknown,
  shouldAutoPromptJob: (jobId: string | number) => boolean,
) {
  return normalizePendingTalentReviews(pending).find((pendingJob) => (
    pendingJob?.jobId != null && shouldAutoPromptJob(pendingJob.jobId)
  ));
}

export function consumePendingFeedbackJobId(storage: Storage = safeLocalStorage) {
  const jobIdStr = storage.getItem('pendingFeedbackJobId');
  if (!jobIdStr) return null;

  const jobId = Number(jobIdStr);
  storage.removeItem('pendingFeedbackJobId');
  return Number.isInteger(jobId) && jobId > 0 ? jobId : null;
}

export function handleViewAllShiftsClick(
  event: Pick<React.MouseEvent<HTMLAnchorElement>, 'preventDefault'>,
  navigate: () => void,
) {
  // Avoid adding a `#` WebView history entry. Android consumes that entry
  // before Cordova can deliver the system Back event to the app router.
  event.preventDefault();
  navigate();
}

export function getChatEligibleJobsForCleanup(jobs: unknown, shiftChatGate: any): any[] {
  const allJobs = Array.isArray(jobs) ? jobs : [];
  if (typeof shiftChatGate?.getChatEligibleJobs === 'function') {
    return shiftChatGate.getChatEligibleJobs(allJobs);
  }

  // The legacy gate owns the post-shift window calculation. If it has not
  // loaded yet, keep the current subscriptions instead of leaving a channel
  // that may still be open for chat.
  return allJobs;
}

export function buildPendingTalentFeedbackNavigationPayload(fullJob: any, fromJobCompleted = false) {
  const payload: { job: any; jobId: number; fromJobCompleted?: boolean } = {
    job: fullJob,
    jobId: Number(fullJob.id),
  };

  if (fromJobCompleted) {
    payload.fromJobCompleted = true;
  }

  return payload;
}

type ChatbaseRole = NonNullable<ChatbaseUserContext['metadata']>['role'];

/**
 * T2-5797: helper to test whether `dateKey` (YYYY-MM-DD) is present in a
 * DailyInstabookSettings array. The API returns `startDate` as a full ISO
 * string (`2026-05-08T00:00:00.000Z`) rather than the calendar key, so a
 * strict `===` comparison silently always returns false.
 */
const isDateKeyMarked = (
  daily: Array<{ startDate?: string | null } | null | undefined> | null | undefined,
  dateKey: string,
): boolean => {
  if (!Array.isArray(daily) || !dateKey) return false;
  const target = dateKey.slice(0, 10);
  return daily.some((d) => {
    const raw = (d && typeof d.startDate === 'string') ? d.startDate : '';
    return !!raw && raw.slice(0, 10) === target;
  });
};

const persistUserAccountSnapshot = (snapshot: any) => {
  if (!snapshot) return;
  safeLocalStorage.setItem('userAccount', JSON.stringify(snapshot));
  try {
    (window as any)?.TempStars?.User?.setCurrentUser?.(snapshot);
  } catch {
    // Legacy in-memory user may be unavailable during early React boot.
  }
};

const removeDailySettingFromSnapshot = (snapshot: any, dateKey: string) => {
  if (!snapshot?.hygienist || !dateKey) return snapshot;
  const existing: Array<any> = Array.isArray(snapshot.hygienist.DailySettings)
    ? snapshot.hygienist.DailySettings
    : [];
  snapshot.hygienist.DailySettings = existing.filter((d: any) => {
    const raw = typeof d?.startDate === 'string' ? d.startDate : '';
    return raw.slice(0, 10) !== dateKey.slice(0, 10);
  });
  return snapshot;
};

const HygienistHome: React.FC = () => {
  const history = useHistory();
  usePhoneVerificationNavigator();
  const location = useLocation<{ qualificationSaved?: boolean }>();
  const [qualificationSavedOpen, setQualificationSavedOpen] = useState(location.state?.qualificationSaved === true);
  useEffect(() => {
    if (location.state?.qualificationSaved) {
      // Keep this confirmation in mounted state, but consume the navigation
      // marker so Back/reload does not announce another save.
      history.replace({ ...location, state: { ...location.state, qualificationSaved: undefined } });
    }
  }, [history, location]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [hireJobsCount, setHireJobsCount] = useState<number>(0);
  const [isActiveUser, setIsActiveUser] = useState<boolean>(false);
  const [chatbaseReady, setChatbaseReady] = useState<boolean>(false);
  const [chatbaseError, setChatbaseError] = useState<boolean>(false);
  const [chatbaseUserContext, setChatbaseUserContext] = useState<ChatbaseUserContext | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatusResponse | null>(null);
  const [membershipLoading, setMembershipLoading] = useState<boolean>(true);
  const [selectionModal, setSelectionModal] = useState<{
    open: boolean;
    offers: HygienistOfferItem[];
    dateKey: string;
    hasAvailable: boolean;
    availableCount: number;
    availableShifts: any[];
    canShowInstabook: boolean;
    pendingAutobookJobId?: number | null;
    /** T2-5797: booked shift id for this date when the talent also has an
     *  Auto-book schedule for it — surfaces "View Booked Shift" in the modal. */
    bookedJobId?: number | null;
  } | null>(null);
  const [membershipDialog, setMembershipDialog] = useState<MembershipDialogCopy | null>(null);
  const {
    getDisplayInfo,
    refreshAll,
    isLoading,
    jobs: calendarJobs,
    getAvailableByDate,
    getBlockedUntil,
    getClassForDate,
    getJobsByDate,
    getJobsByDateMap,
    getOffersByDate,
    dataVersion,
    availableShiftsCount,
    isInstabookCandidateDate,
    getActiveOfferCountByDate,
    getAvailableShiftsForDate,
    phoneVerificationRequired,
    isRelevantJob,
    isAvailableOnlyJob,
    dropAvailableJob,
  } = useHygienistCalendarData() as any;
  const {
    isAvailable,
    isMembershipBlocked: isAvailableNowMembershipBlocked,
    blockedDaysRemaining,
    isLoading: isToggleLoading,
    toggle,
    showInfo,
    ui: availableUi,
  } = useAvailableNow({ membership: membershipStatus, membershipLoading });

  const openPendingTalentReview = useCallback(async (jobId: number, fromJobCompleted = false) => {
    if (!jobId) return false;
    try {
      const fullJob = await Api.getJobDetails(jobId);
      if (!fullJob?.id) return false;
      try { safeLocalStorage.setItem('feedbackNav', '1'); } catch {}
      // Overlay mode matches legacy F7 surveyButtonHandler — full index.html reload
      // via goReact() is unreliable on iOS Cordova/WKWebView.
      (window as any).TempStars?.App?.goReact(
        'hygienistFeedback',
        buildPendingTalentFeedbackNavigationPayload(fullJob, fromJobCompleted),
        { overlay: true },
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  // Apply unread chat count badges per date
  const applyUnreadBadges = useCallback(async (existingJobs?: any[]) => {
    try {
      const conv = (window as any)?.TempStars?.Conversations;
      // T2-7436: once the calendar has loaded, its list is the answer even when
      // empty; only fetch before the first load.
      const allJobs = existingJobs?.length
        ? existingJobs
        : (calendarJobs?.length || dataVersion > 0 ? (calendarJobs || []) : await Api.getHygienistJobs(''));

      try {
        const hygienistId = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienistId;
        const chatEligibleJobs = getChatEligibleJobsForCleanup(
          allJobs,
          (window as any)?.TempStars?.ShiftChatGate,
        );
        await conv?.unsubscribeFromUnusedChannels?.(chatEligibleJobs, null, hygienistId);
      } catch {}

      const cells = document.querySelectorAll('.unread-message-count');
      cells.forEach((n) => n.parentElement?.removeChild(n));
      const unread = await (window as any)?.TempStars?.Conversations?.getUnreadMessages?.();
      const perDate = (window as any)?.TempStars?.ChatPage?.countUnreadMessagesPerDate?.(allJobs, unread);
      if (perDate) {
        Object.keys(perDate).forEach((dk) => {
          const count = perDate[dk];
          if (count > 0) {
            (window as any)?.TempStars?.ChatPage?.addingUnreadMessageCountBadge?.(dk, count);
          }
        });
      }
    } catch {}
  }, [calendarJobs, dataVersion]);

  // Prevent stale auto-redirects to hygienistFeedback from legacy/localStorage
  useEffect(() => {
    try {
      const pd = safeLocalStorage.getItem('pageData');
      if (pd) {
        const parsed = JSON.parse(pd);
        if (parsed && parsed.page === 'hygienistFeedback') {
          safeLocalStorage.removeItem('pageData');
        }
      }
    } catch {}
  }, []);

  // Targeted hand-off from Mark-as-Complete: if the talent just completed a
  // shift, CompleteShift stashes `pendingFeedbackJobId` in localStorage and
  // sends them back here. Once the calendar has rendered, surface the feedback
  // modal for that exact shift — bypassing the one-time-prompts cascade (which
  // has gates like Verified / isOtherModalOpen / server pending-review query
  // that can silently drop a freshly-completed shift).
  const hasOpenedPostCompleteFeedback = useRef(false);
  useEffect(() => {
    if (qualificationSavedOpen || isLoading || hasOpenedPostCompleteFeedback.current) return;
    let jobId: number | null = null;
    try { jobId = consumePendingFeedbackJobId(); } catch {}
    if (!jobId) return;
    hasOpenedPostCompleteFeedback.current = true;
    void openPendingTalentReview(jobId, true);
  }, [qualificationSavedOpen, isLoading, openPendingTalentReview]);

  // T2-7436: refresh only for this talent's own shifts and offers, or a new
  // shift they could take; the hook spreads refreshes out over a few seconds.
  // Unread badges repaint after every load (the dataVersion effect below).
  useHygienistSocketEvents({
    onRefresh: refreshAll,
    isRelevantJob,
    isAvailableOnlyJob,
    onJobUnavailable: dropAvailableJob,
  });

  // Initial data load (placeholder: only Job Board badge count for now)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        try {
          const user = await Api.getUser();
          persistUserAccountSnapshot(user);
        } catch {}
        const res = await Api.getAvailableHireJobsCount();
        if (mounted) setHireJobsCount(Number(res?.result ?? 0));
      } catch (e) {
        // noop
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
      if (!currentUser) return;
      let role: ChatbaseRole = 'hygienist';
      if (currentUser?.assistant) {
        role = 'assistant';
      } else if (!currentUser?.hygienist) {
        role = 'member';
      }
      const displayName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
      setChatbaseUserContext({
        userId: currentUser?.id ? String(currentUser.id) : undefined,
        metadata: {
          name: displayName || undefined,
          email: currentUser?.email,
          role,
          membershipTier: membershipStatus?.currentStatusCode || undefined,
          region: currentUser?.hygienist?.province || currentUser?.hygienist?.state || undefined,
        },
      });
    } catch (error) {
      console.warn('Unable to derive Chatbase user metadata', error);
    }
  }, [membershipStatus]);

  // Deep-link support: if ?shiftDate=YYYY-MM-DD present, open Available Jobs and clear URL
  useEffect(() => {
    try {
      const params = new URL(window.location.href).searchParams;
      const shiftDate = params.get('shiftDate');
      if (shiftDate) {
        history.push({ pathname: 'availableJobs', state: { date: shiftDate, withDate: true } });
        try { window.history.pushState('object', document.title, window.location.origin); } catch {}
      }
    } catch {}
  }, [history]);

  // Canonical membership owns Autobook activation eligibility. Raw
  // Hygienist.status may diverge during cutover and must not affect this CTA.
  useEffect(() => {
    if (isLoading) return;
    const completedLast30 = (calendarJobs || []).filter(
      (job: any) => Number(job.status) === 4 && dayjs().diff(dayjs(job.startDate), 'day') < 30,
    ).length;
    setIsActiveUser(canShowAutobookActivationCta(membershipStatus, completedLast30));
  }, [isLoading, calendarJobs, membershipStatus]);

  // One-time prompts: announcement, pending reviews, work experience, languages, grad year, resume/profile, invoice prompts
  // Invoice prompts use shared calendarJobs instead of a separate fetch
  const hasRunOneTimePrompts = useRef(false);
  useEffect(() => {
    if (qualificationSavedOpen || isLoading || hasRunOneTimePrompts.current) return;
    hasRunOneTimePrompts.current = true;
    let cancelled = false;
    (async () => {
      try {
        const app = (window as any)?.app;
        const user = await Api.getUser();
        if (cancelled) return;

        // T2-6044: Legacy Survey/SurveyAnswer experience is hidden from talent.
        // The engagedPromotion CTA used to open a modal that would route to the
        // legacy `survey` page; removed here so no legacy surveys are surfaced.
        // Data tables (Survey, SurveyAnswers) remain intact for future repurposing.

        // Announcement overlay
        try {
          const photoUrl: string | undefined = user?.hygienist?.photoAnnounceUrl || undefined;
          const linkUrl: string | undefined = user?.hygienist?.announceUrl || undefined;
          if (photoUrl) {
            setAnnouncement({ open: true, photoUrl, linkUrl });
          }
        } catch {}

        // Pending Reviews prompt (queue first item) → direct React feedback form
        if (user?.hygienist?.Verified && !shouldSuppressTalentFeedbackHomePrompt()) {
          try {
            // Ensure no stale flag allows bypass; always clear on mount already
            const pending = await Api.getPendingTalentReviews();
            if (cancelled) return;

            // Normalize API shape (array or { success, data }) and ensure array
            // Only show if no other critical modal is open
            const isOtherModalOpen = instaVideoOpen || instaTermsOpen || instaSuccessOpen || !!emptyDayModal?.open || manualShiftCongratsOpen || manualShiftInfoOpen;
            if (!isOtherModalOpen) {
              const first = selectHomePendingTalentReview(pending, shouldAutoPromptTalentFeedbackJob);
              if (first?.jobId) {
                const opened = await openPendingTalentReview(Number(first.jobId));
                if (opened) {
                  markTalentFeedbackHomePromptShown(first.jobId);
                  return;
                }
              }
            }
          } catch (e) {}
        }

        // Work Experience prompt
        try {
          const work = await Api.getWorkExperience();
          if (!cancelled && (!work || work.length === 0)) {
            setWorkExpOpen(true);
            return;
          }
        } catch {}

        // Languages prompt
        try {
          const res = await Api.getNeedsLanguages();
          if (!cancelled && res?.needsLanguages) {
            setLanguagesOpen(true);
            return;
          }
        } catch {}

        // Graduation Year prompt (narrow 2023-10..2024-03 localStorage window)
        // replaced by T2-6634 server-backed education credentials confirmation below.

        // T2-6634: required one-time confirmation when educationCredentialsConfirmedOn is unset
        try {
          const hyg = user?.hygienist;
          if (!cancelled && hyg && !hyg.educationCredentialsConfirmedOn) {
            setGradYearOpen(true);
            return;
          }
        } catch {}

        // Invoice prompts (uses shared calendarJobs)
        try {
          (window as any).TempStars = (window as any).TempStars || {};
          (window as any).TempStars.Hygienist = (window as any).TempStars.Hygienist || {};
          (window as any).TempStars.Hygienist.invoiceStatus = (window as any).TempStars.Hygienist.invoiceStatus || {};
          const invoiceStatus = (window as any).TempStars.Hygienist.invoiceStatus;
          const pending: any[] = (calendarJobs || []).filter((j: any) => j.status === 4 && !j.invoiceId && !j.invoiceLater && !invoiceStatus[j.id]);
          if (pending.length > 0) {
            setInvoiceQueue(pending)
            setInvoiceIdx(0)
            setInvoiceOpen(true)
          }
        } catch {}

      } catch {}
    })();
    return () => { cancelled = true; };
  }, [qualificationSavedOpen, isLoading, calendarJobs, openPendingTalentReview]);

  // Verification description: if the Talent is not Verified, trigger the full SMS verification modal chain.
  // Respects a per-session "deferred" flag set in sessionStorage when the user taps
  // "I'll do this later" (or "not now" from the Later-perhaps modal). This way the
  // home page does not keep nagging them every time they navigate back, while
  // AvailableShifts → shift tap still enforces verification (AC5 of T2-5704).
  const [exitReactState, setExitReactState] = useState<boolean>(false);
  // The legacy "confirm your mobile" flow; its Profile and Support links open in React.
  const openPhoneVerification = useCallback(() => {
    (window as any).TempStars?.Verification?.VerificationDescription(
      setExitReactState,
      () => { history.push('hygienistProfile'); },
      () => { history.push('hygienistContactSupport'); }
    );
  }, [history]);
  // The first-load prompt runs once; it calls the latest helper through a ref so
  // a new history object can never cancel it while the member is loading.
  const openPhoneVerificationRef = useRef(openPhoneVerification);
  openPhoneVerificationRef.current = openPhoneVerification;
  const hasRunVerification = useRef(false);
  useEffect(() => {
    if (qualificationSavedOpen || isLoading || hasRunVerification.current) return;
    hasRunVerification.current = true;
    let cancelled = false;
    (async () => {
      try {
        const user = await Api.getUser();
        if (cancelled) return;
        const verified = !!user?.hygienist?.Verified;
        const deferred = (() => {
          try { return safeSessionStorage.getItem('ts_verification_home_deferred') === '1'; } catch { return false; }
        })();
        if (!verified && !deferred) {
          openPhoneVerificationRef.current();
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [qualificationSavedOpen, isLoading]);

  useEffect(() => {
    if (exitReactState) {
      history.push('loading');
      exitReact('contact-support');
      setExitReactState(false);
    }
  }, [exitReactState, history]);

  const handleMonthChange = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const [instaOptions, setInstaOptions] = React.useState<{ open: boolean; dateKey: string; marked: boolean } | null>(null)
  const [hoursPicker, setHoursPicker] = React.useState<{
    open: boolean;
    dateKey: string;
    marked: boolean;
    globalSettingsPreview?: { maxDistance?: number; minShiftLength?: number; hourlyRate?: number } | null;
  } | null>(null)
  const [noSettingsPromptOpen, setNoSettingsPromptOpen] = React.useState<boolean>(false)
  const [autobookWindowError, setAutobookWindowError] = useState(false);
  const isWithinTalentAutobookWindow = useCallback((dateKey: string) => {
    const talent = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
    return isWithinAutobookWindow(dateKey, new Date(), talent?.autobookTimeZone);
  }, []);

  // React replacements for legacy Framework7 modals triggered by onClick actions
  const [instaVideoOpen, setInstaVideoOpen] = React.useState<boolean>(false)
  const [instaVideoUrl, setInstaVideoUrl] = React.useState<string>('')
  const [instaTermsOpen, setInstaTermsOpen] = React.useState<boolean>(false)
  const [agreementChecks, setAgreementChecks] = React.useState<{ a: boolean; b: boolean; c: boolean }>({ a: false, b: false, c: false })
  const [instaSuccessOpen, setInstaSuccessOpen] = React.useState<boolean>(false)

  const [emptyDayModal, setEmptyDayModal] = React.useState<{ open: boolean; dateKey: string; canShowInstabook: boolean } | null>(null)
  const [jobBoardBlockedOpen, setJobBoardBlockedOpen] = React.useState<boolean>(false)
  const [manualShiftCongratsOpen, setManualShiftCongratsOpen] = React.useState<boolean>(false)
  const [manualShiftInfoOpen, setManualShiftInfoOpen] = React.useState<boolean>(false)
  const [penaltyAlert, setPenaltyAlert] = React.useState<{ open: boolean; message: string } | null>(null)
  const [announcement, setAnnouncement] = React.useState<{ open: boolean; photoUrl?: string; linkUrl?: string } | null>(null)
  const [workExpOpen, setWorkExpOpen] = React.useState<boolean>(false)
  const [languagesOpen, setLanguagesOpen] = React.useState<boolean>(false)
  const [gradYearOpen, setGradYearOpen] = React.useState<boolean>(false)
  const [invoiceQueue, setInvoiceQueue] = React.useState<any[]>([])
  const [invoiceIdx, setInvoiceIdx] = React.useState<number>(0)
  const [invoiceOpen, setInvoiceOpen] = React.useState<boolean>(false)
  const [jobBoardHelpOpen, setJobBoardHelpOpen] = React.useState<boolean>(false)
  const [noShiftsModalOpen, setNoShiftsModalOpen] = React.useState<boolean>(false);
  const [userCity, setUserCity] = React.useState<string>('');

  useEffect(() => {
    let mounted = true;

    const loadMembership = async () => {
      setMembershipLoading(true);
      try {
        const membership = await Api.getMembershipStatus();
        if (mounted) {
          setMembershipStatus(membership);
        }
      } catch {
        if (mounted) {
          setMembershipStatus(null);
        }
      } finally {
        if (mounted) {
          setMembershipLoading(false);
        }
      }
    };

    loadMembership();
    return () => {
      mounted = false;
    };
  }, []);


  // Check for "No Shifts Available" modal condition
  useEffect(() => {
    if (qualificationSavedOpen || isLoading || membershipLoading) return;
    const isEnforcedMembership = membershipStatus?.mode === 'enforced';
    if (isEnforcedMembership && membershipStatus?.tempShiftAccess === 'restricted') {
      return;
    }
    // ValueGives reform overlay states own the zero-shifts UX for provisional
    // users in course_required / restricted_reentry / not_a_fit — don't show
    // the generic "No Shifts Available" modal on top of the banner.
    if (isEnforcedMembership && membershipStatus?.overlay?.active) {
      return;
    }
    // T2-7517: the server holds shifts back until the phone is verified; the
    // calendar shows the days that have them. The Welcome would be wrong here.
    if (phoneVerificationRequired) return;
    // If available shifts count is 0, verify if user has seen the modal
    if (availableShiftsCount === 0) {
      Api.getUser().then((user) => {
        if (user?.hygienist && !user.hygienist.isNoShiftsAvailableModalSeen) {
          setUserCity(user.hygienist.city || 'your area');
          setNoShiftsModalOpen(true);
          // Track event parity with legacy
          try {
            (window as any).TempStars?.Analytics?.track?.('No Shifts Popup Shown', {
                userId: user.hygienist.id,
                userType: user.hygienist.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST ? 'Hygienist' : 'Dental Assistant',
                city: user.hygienist.city || '',
                province: user.hygienist.province || '',
                country: user.hygienist.country === 1 ? 'Canada' : 'United States', // 1 is CA in legacy constants usually, verify if needed but this is safe
                postalCode: user.hygienist.postalCode || '',
                hourOfDay: new Date().getHours()
            });
          } catch {}
        }
      }).catch(() => {});
    }
  }, [qualificationSavedOpen, isLoading, availableShiftsCount, phoneVerificationRequired, membershipLoading, membershipStatus?.mode, membershipStatus?.tempShiftAccess, membershipStatus?.overlay?.active]);

  const handleDateClick = useCallback((date: Date) => {
    const app = (window as any)?.app;
    const hygienist = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;

    const statusClass: string = getClassForDate?.(dateKey) || '';
    const jobs = getJobsByDate?.(dateKey) || [];
    const allOffers = getOffersByDate?.(dateKey) || [];
    const availableCount = (getAvailableByDate?.() || {})[dateKey] || 0;
    const hasAvailable = availableCount > 0;

    const withinAutobookWindow = isWithinTalentAutobookWindow(dateKey);
    const membershipAllowsInstabook = canUseInstabookByMembership({
      membership: membershipStatus,
    });
    const membershipAllowsAutobookSchedule = canManageAutobookScheduleByMembership(membershipStatus);
    // Booking and schedule editing retain separate membership gates, but share
    // the same fourteen local calendar dates (today through today + 13).
    const canShowInstabook = (hygienist?.InstabookActivated > 0) && membershipAllowsInstabook && withinAutobookWindow;
    const canEditAutoBookSchedule = (hygienist?.InstabookActivated > 0) && membershipAllowsAutobookSchedule && withinAutobookWindow;
    
    // Filter out active offers for past dates (legacy parity). Expired offers
    // remain viewable so talent can open Expired Custom Offer details.
    const isPastDate = dayjs(dateKey).isBefore(dayjs(), 'day');
    const offers = isPastDate
      ? allOffers.filter((o: any) => Number(o?.status) === PARTIAL_OFFER_STATUS.EXPIRED)
      : allOffers;

    // Completed → navigate to React Completed Job page with jobId
    if (statusClass.includes('calendar-completed')) {
      const completedJob = jobs.find((j: any) => j.status === 4);
      if (completedJob?.id) {
        history.push({ pathname: 'completeJob', state: { jobId: completedJob.id } });
        return;
      }
    }
    // Confirmed
    if (statusClass.includes('calendar-booked')) {
      const confirmed = jobs.find((j: any) => j.status === 3);
      if (confirmed) {
        // T2-5797: when the talent also has their own Auto-book schedule on
        // this booked date, give them the choice — view the booked shift OR
        // set/edit that day's schedule — instead of silently opening the
        // confirmed page and hiding the schedule options.
        const hygSnapshot = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
        const dailyOnBookedDate = isDateKeyMarked(
          (hygSnapshot?.DailySettings || []) as Array<{ startDate?: string | null } | null | undefined>,
          dateKey,
        );
        const canEditOnBookedDate = !!hygSnapshot?.InstabookActivated
          && canManageAutobookScheduleByMembership(membershipStatus);
        if (dailyOnBookedDate && canEditOnBookedDate) {
          setSelectionModal({
            open: true,
            offers: [],
            dateKey,
            hasAvailable: false,
            availableCount: 0,
            availableShifts: [],
            canShowInstabook: true,
            pendingAutobookJobId: null,
            bookedJobId: confirmed.id,
          });
          return;
        }
        history.push({ pathname: 'confirmedOffer', state: { jobId: confirmed.id, fromHome: true } });
        return;
      }
    }

    const blockedUntilMs = getBlockedUntil?.() ?? parseBlockedUntilMs(hygienist?.blockedUntil);
    const blockedUntilRaw = hygienist?.blockedUntil ?? blockedUntilMs;
    if (
      blockedUntilMs != null
      && isDateBlockedByCancellationCooldown(dateKey, blockedUntilRaw)
      && !dayjs(dateKey).isBefore(dayjs(), 'day')
    ) {
      setMembershipDialog(getCancellationCooldownDialogCopy(blockedUntilRaw));
      return;
    }

    // Gate shift-detail / offer navigation for unverified Talent.
    // Mirrors AvailableJobsPage.goToPage (T2-5704): completed / blocked / confirmed
    // days above are already safe to open; everything below (InstaBook candidate,
    // Instabooked, Offers/Available, autobook setup) can lead to sending offers
    // or booking, so route through the SMS verification modal chain instead.
    // T2-7517: the server's answer counts too (phoneVerificationRequired), so a
    // stale user snapshot cannot open a locked day.
    if (phoneVerificationRequired || !hygienist?.Verified) {
      openPhoneVerification();
      return;
    }

    // InstaBook candidate (pending DO booking) - with Auto-book, offer View Shifts vs schedule.
    if (canShowInstabook && isInstabookCandidateDate?.(dateKey)) {
      const availableShiftsForDate = availableCount < 4
        ? (getAvailableShiftsForDate?.(dateKey) || [])
        : [];
      const effectiveAvailableCount = Math.max(availableCount, availableShiftsForDate.length);
      setSelectionModal({
        open: true,
        offers: [],
        dateKey,
        hasAvailable: hasAvailable || effectiveAvailableCount > 0,
        availableCount: effectiveAvailableCount,
        availableShifts: availableShiftsForDate,
        canShowInstabook: true,
        pendingAutobookJobId: null,
      });
      return;
    }

    // Instabooked (office request pending talent confirm/reject)
    if (statusClass.includes('calendar-instabooked')) {
      const insta = jobs.find((j: any) => j.Instabook === 1 || (j.Autobook === 1 && Number(j.status) === 2));
      if (insta && canShowInstabook) {
        const availableShiftsForDate = (hasAvailable && availableCount < 4)
          ? (getAvailableShiftsForDate?.(dateKey) || [])
          : [];
        setSelectionModal({
          open: true,
          offers: (offers || []).map((o: any) => ({ id: o?.id, jobId: o.jobId, status: Number(o?.status), hourlyRate: o?.hourlyRate, offeredStartTime: o?.offeredStartTime, offeredEndTime: o?.offeredEndTime, job: o?.job })) as any,
          dateKey,
          hasAvailable,
          availableCount,
          availableShifts: availableShiftsForDate,
          canShowInstabook: true,
          pendingAutobookJobId: insta.id,
        });
        return;
      }
      if (insta) {
        history.push({
          pathname: 'hygienistInstabookConfirmation',
          state: { jobId: insta.id, job: insta },
        });
        return;
      }
    }

    // Existing future hours stay visible and removable beyond the write window.
    const hasOwnAutoBookSchedule = (hygienist?.InstabookActivated > 0)
      && membershipAllowsAutobookSchedule && !isPastDate && isDateKeyMarked(
      (hygienist?.DailySettings || []) as Array<{ startDate?: string | null } | null | undefined>,
      dateKey,
    );

    // Offers/Available selections
    if ((offers && offers.length) || hasAvailable) {
      // Schedule editing does not require the Pro/Elite booking capability.
      // Keep both actions reachable for available-only and single-offer dates.
      const canOpenAutoBookSchedule = canEditAutoBookSchedule || hasOwnAutoBookSchedule;
      const shouldShowModal =
        ((offers?.length || 0) >= 2) ||
        ((offers?.length || 0) > 0 && hasAvailable) ||
        canOpenAutoBookSchedule;

      if (shouldShowModal) {
        const availableShiftsForDate = (hasAvailable && availableCount < 4)
          ? (getAvailableShiftsForDate?.(dateKey) || [])
          : [];
        setSelectionModal({
          open: true,
          offers: (offers || []).map((o: any) => ({ id: o?.id, jobId: o.jobId, status: Number(o?.status), hourlyRate: o?.hourlyRate, offeredStartTime: o?.offeredStartTime, offeredEndTime: o?.offeredEndTime, job: o?.job })) as any,
          dateKey,
          hasAvailable,
          availableCount,
          availableShifts: availableShiftsForDate,
          canShowInstabook: canOpenAutoBookSchedule,
          pendingAutobookJobId: null,
        });
        return;
      }
      if (hasAvailable && (offers?.length || 0) === 0) {
        /** T2-5760: only available shifts and no autobook – jump straight to filtered list */
        goToAvailableShifts({ date: dateKey });
      } else if (!hasAvailable && (offers?.length || 0) === 1) {
        // One offer and no available shifts – go straight to that offer
        const off = offers[0];
        history.push({ pathname: 'partialOffer', state: { jobId: off.jobId, fromHome: true } });
      }
      return;
    }

    // Existing hours can be removed even if the date is outside the window or
    // the global settings are no longer configured.
    const flagOn = (window as any)?.TempStars?.FeatureFlags?.reactAutoBookEnabled === true
    if (hasOwnAutoBookSchedule || canEditAutoBookSchedule || (canShowInstabook && flagOn)) {
      const hasGlobal = !!hygienist?.GlobalSettings?.maxDistance;
      const daily = (hygienist?.DailySettings || []) as Array<{ startDate: string }>
      // T2-5797: API returns startDate as a full ISO string (e.g.
      // "2026-05-08T00:00:00.000Z") while dateKey is "YYYY-MM-DD". A strict
      // equality check therefore never matches and the modal incorrectly
      // hides the "Remove This Date" button + falls into the "create" path
      // every time. Normalize to the first 10 chars before comparing.
      const isMarked = isDateKeyMarked(daily, dateKey);
      if (hasGlobal || isMarked) {
        setInstaOptions({ open: true, dateKey, marked: isMarked })
      } else {
        setNoSettingsPromptOpen(true)
      }
      return;
    }

    setEmptyDayModal({ open: true, dateKey, canShowInstabook: canShowInstabook || canEditAutoBookSchedule });
  }, [getBlockedUntil, getClassForDate, getJobsByDate, getOffersByDate, getAvailableByDate, getAvailableShiftsForDate, history, membershipStatus, isInstabookCandidateDate, isWithinTalentAutobookWindow, phoneVerificationRequired, openPhoneVerification]);

  const handleChatbaseLoad = useCallback(() => {
    setChatbaseReady(true);
    setChatbaseError(false);
  }, []);

  const handleChatbaseError = useCallback((error: Error) => {
    console.error('Chatbase widget failed to load', error);
    setChatbaseError(true);
  }, []);

  const openContactSupport = useCallback(() => {
    requestChatbaseSupport();
    (window as any).TempStars?.App?.goReact('hygienistContactSupport');
  }, []);

  const onHelpClick = useCallback(() => {
    if (!chatbaseError && openChatbaseWidget()) {
      return;
    }
    if (chatbaseError) {
      alert('AI assistant is temporarily unavailable. Redirecting to support center.');
    } else if (!chatbaseReady) {
      alert('AI assistant is still loading. Redirecting to support center.');
    }
    openContactSupport();
  }, [openContactSupport, chatbaseError, chatbaseReady]);

  // Centralized navigation to Available Jobs
  const goToAvailableShifts = useCallback((filter?: { date?: string; isAvailable?: boolean }) => {
    history.push({ pathname: 'availableJobs', state: { ...(filter || {}), withDate: !!filter?.date } });
  }, [history]);

  const onViewAllShifts = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    handleViewAllShiftsClick(event, () => {
      // T2-7517 (Holly, 2026-10-09): while the server holds shifts back for an
      // unverified phone, "View All Shifts" asks for verification, like a tap on
      // a green day, instead of opening an empty list.
      if (phoneVerificationRequired) {
        openPhoneVerification();
        return;
      }
      goToAvailableShifts();
    });
  }, [goToAvailableShifts, openPhoneVerification, phoneVerificationRequired]);

  const handleOpenManualShift = useCallback(() => {
    setManualShiftCongratsOpen(true);
  }, []);

  const handleOpenManualShiftInfo = useCallback(() => {
    setManualShiftInfoOpen(true);
  }, []);

  const handleCloseSelectionModal = useCallback(() => {
    setSelectionModal(null);
  }, []);

  const handleSelectOffer = useCallback((jobId: number) => {
    setSelectionModal(null);
    history.push({ pathname: 'partialOffer', state: { jobId } });
  }, [history]);

  const handleSelectAvailableJob = useCallback((jobId: number) => {
    setSelectionModal(null);
    history.push({ pathname: 'availableJob', state: { id: jobId, filter: { date: selectionModal?.dateKey }, isFavourite: false } });
  }, [history, selectionModal?.dateKey]);

  const handleViewMoreShifts = useCallback(() => {
    setSelectionModal(null);
    goToAvailableShifts({ date: selectionModal?.dateKey });
  }, [goToAvailableShifts, selectionModal?.dateKey]);

  const handleInstaBook = useCallback(() => {
    const hyg = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
    const hasGlobal = !!hyg?.GlobalSettings?.maxDistance;
    const dateKey = selectionModal?.dateKey || '';
    const daily = (hyg?.DailySettings || []) as Array<{ startDate: string }>;
    // T2-5797: see comment above; normalize ISO startDate to YYYY-MM-DD.
    const isMarked = isDateKeyMarked(daily, dateKey);
    if (!isMarked && !isWithinTalentAutobookWindow(dateKey)) {
      setAutobookWindowError(true);
    } else if (hasGlobal || isMarked) {
      setInstaOptions({ open: true, dateKey, marked: isMarked });
    } else {
      setNoSettingsPromptOpen(true);
    }
    setSelectionModal(null);
  }, [selectionModal?.dateKey, isWithinTalentAutobookWindow]);

  const handleCloseManualShiftCongrats = useCallback(() => {
    setManualShiftCongratsOpen(false);
  }, []);

  const handleContinueManualShift = useCallback(() => {
    setManualShiftCongratsOpen(false);
    history.push({ pathname: 'manualBookShift' });
  }, [history]);

  const handleCloseManualShiftInfo = useCallback(() => {
    setManualShiftInfoOpen(false);
  }, []);

  const onJobBoardClick = useCallback(async () => {
    const goToJobBoard = async () => {
      const hyg = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
      if (!hyg?.isJobBoardHelpMsgSeen) {
        setJobBoardHelpOpen(true)
      } else {
        (window as any).TempStars?.App?.goReact('jobPostsList');
      }
    };

    await goToJobBoard();
  }, []);

  // Build a minimal date → marker jobs map based on hygienist status classes for dots/colors
  const dateMarkerMap = useMemo(() => {
    const start = dayjs(currentDate).startOf('month').startOf('week');
    const end = dayjs(currentDate).endOf('month').endOf('week');
    const map: Record<string, Array<{ jobId: number; effectiveStatus: number }>> = {};
    let d = start;
    const mapStatusClassToStatuses = (statusClass: string): number[] => {
      switch (statusClass) {
        case 'calendar-booked':
          return [JOB_STATUS.BOOKED];
        case 'calendar-completed':
          return [JOB_STATUS.COMPLETED];
        case 'calendar-custom':
          return [JOB_STATUS.CUSTOM];
        case 'calendar-expired':
          return [JOB_STATUS.EXPIRED];
        case 'calendar-custom-expired':
          return [JOB_STATUS.CUSTOM, JOB_STATUS.EXPIRED];
        case 'calendar-expired-custom':
          return [JOB_STATUS.EXPIRED, JOB_STATUS.CUSTOM];
        case 'calendar-custom-custom':
          return [JOB_STATUS.CUSTOM, JOB_STATUS.CUSTOM];
        case 'calendar-expired-expired':
          return [JOB_STATUS.EXPIRED, JOB_STATUS.EXPIRED];
        case 'calendar-instabooked':
          return [JOB_STATUS.BOOKED];
        case 'calendar-available':
          return [JOB_STATUS.POSTED];
        default:
          return [];
      }
    };
    while (d.isBefore(end) || d.isSame(end, 'day')) {
      const key = d.format('YYYY-MM-DD');
      const css = getClassForDate?.(key) || '';
      const statuses = mapStatusClassToStatuses(css);
      if (statuses.length > 0) {
        map[key] = statuses.slice(0, 3).map((st, idx) => ({ jobId: -idx - 1, effectiveStatus: st }));
      }
      d = d.add(1, 'day');
    }
    return map;
  }, [currentDate, getClassForDate]);

  useEffect(() => {
    applyUnreadBadges(calendarJobs);
  }, [currentDate, dataVersion, applyUnreadBadges, calendarJobs]);

  // Subscribe to chat events for unread badges lifecycle parity
  useEffect(() => {
    const conv = (window as any)?.TempStars?.Conversations;
    if (!conv) return;
    const handler = () => { applyUnreadBadges(); };
    try {
      conv.subscribeToNewMessages?.(handler);
      conv.subscribeToNewChannels?.(handler);
    } catch {}
    return () => {
      try {
        conv.unsubscribeFromNewMessages?.(handler);
        conv.unsubscribeFromNewChannels?.(handler);
      } catch {}
    };
  }, [applyUnreadBadges]);

  // Get current user info for display
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const hygienist = currentUser?.hygienist;
  const firstName = hygienist?.firstName || '';
  const lastName = hygienist?.lastName || '';
  const photoUrl = hygienist?.photoUrl || undefined;
  const membershipBanner = membershipStatus
    ? getMembershipBannerCopy(membershipStatus, { firstName })
    : null;
  // Duplicate-review hold gets its own banner treatment: the full explanation
  // plus Reset Password / Contact Support (My Stats is about FFS scores and
  // says nothing useful about a duplicate hold).
  const isDuplicateHold = !!membershipStatus?.overlay?.active
    && membershipStatus?.overlay?.state === 'duplicate_review';
  const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
                       hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

  const handleMembershipAction = useCallback((path: string) => {
    openMembershipPath(history, path);
  }, [history]);

  useEffect(() => {
    blurActiveElementAndHideKeyboard();
  }, []);

  return (
    <>
    <BrandedAlertModal
      isOpen={qualificationSavedOpen}
      headerLabel="Profile saved"
      title="Qualification saved"
      ariaLabel="Qualification saved confirmation"
      actions={[{
        label: 'Continue', ariaLabel: 'Continue after saving qualifications', variant: 'primary',
        onClick: () => setQualificationSavedOpen(false),
      }]}
    >
      <p role="status" data-testid="qualification-saved-status">Your qualification, skills and languages have been saved.</p>
    </BrandedAlertModal>
    <ChatbaseWidget
      userContext={chatbaseUserContext}
      onLoad={handleChatbaseLoad}
      onError={handleChatbaseError}
      onContactSupport={openContactSupport}
    />
    <div className={styles.hygienistHomePage}>
      <TalentNavbar
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        isHygOrAsst={isHygOrAsst}
        instabookActivated={hygienist?.InstabookActivated === 1}
        isTestAccount={(hygienist as any)?.isTest === 1}
        hireJobsCount={hireJobsCount}
        onAddShiftClick={handleOpenManualShift}
        onJobBoardClick={onJobBoardClick}
        hideAddShiftOnMobile
      />

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Welcome Section */}
        <section className={styles.welcomeSection}>
          <div className={styles.welcomeHeader}>
            <h2 className={styles.welcomeText}>
              Welcome back, {firstName} <WaveHandIcon className={styles.waveEmoji} />
            </h2>
          </div>
          <p className={styles.instructions}>
            Tap <span className={styles.greenText}>green calendar</span> day to view temping shifts for that date, or <a href="#" id="hygienist-home-available-jobs-button" data-testid="hygienist-home-available-jobs-button" className={styles.viewAllLink} onClick={onViewAllShifts}>View All Shifts</a>
          </p>
        </section>

        {/* Status Banner */}
        {shouldShowShiftVisibilityRestrictionBanner(membershipStatus) ? (
          <div
            className={styles.messageBanner}
            style={{
              backgroundColor: membershipBanner?.tone === 'error' ? '#FEF3F3' : membershipBanner?.tone === 'warning' ? '#FFF8E1' : '#E8F4FD',
              borderColor: membershipBanner?.tone === 'error' ? '#F5C2C7' : membershipBanner?.tone === 'warning' ? '#F0DFA0' : '#B6DAFE',
              color: membershipBanner?.tone === 'error' ? '#7F1D1D' : membershipBanner?.tone === 'warning' ? '#8A5B00' : '#0B4598',
              textAlign: 'left',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>
              {isDuplicateHold || membershipBanner?.tone === 'error' ? membershipBanner?.title : 'Account status notice'}
            </div>
            {isDuplicateHold ? (
              <div>
                <div>{membershipBanner?.body}</div>
                <div style={{ marginTop: '10px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={styles.linkLike}
                    onClick={() => history.push('ForgotPassword')}
                  >
                    Reset password
                  </button>
                  <button
                    type="button"
                    className={styles.linkLike}
                    onClick={() => handleMembershipAction('hygienistContactSupport')}
                  >
                    Contact support
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {membershipBanner?.inlineAction ? (
                  <MembershipBannerMessage copy={membershipBanner} />
                ) : (
                  <>
                    You may not be seeing all available shifts due to your account status.{' '}
                    <button
                      type="button"
                      className={styles.linkLike}
                      onClick={() => handleMembershipAction('myStats')}
                    >
                      Click here for details.
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* Available Today Toggle */}
        {!isAvailableNowMembershipBlocked && (
          <div className={styles.availableNowCard}>
            <div className={styles.availableNowInfo}>
              <div className={styles.availableNowTitle}>
                Available Today
                <img
                  src={infoIcon}
                  alt="info"
                  className={styles.infoIcon}
                  onClick={showInfo}
                />
              </div>
              <div className={styles.availableNowSubtitle}>I am available for temping today!</div>
            </div>
            <label className={styles.switch} aria-label="Available Today toggle">
              <input
                type="checkbox"
                checked={!!isAvailable}
                onChange={(e) => toggle(e.target.checked)}
                disabled={isToggleLoading || membershipLoading}
                aria-label="Toggle Available Today status"
              />
              <span className={styles.slider}></span>
            </label>
          </div>
        )}

        {/*
          T2-5859: Autobook Activation CTA was previously rendered below the
          calendar. Holly asked to move it up here — just under the Available
          Today toggle — so talent see the activation prompt before scrolling
          the full month grid.
        */}
        {isActiveUser && (hygienist?.InstabookActivated ?? 0) === 0 && (
          <div className={styles.autobookRow}>
            <button className={styles.autobookCta} onClick={async () => {
              try {
                const url = await Api.getInstabookVideoLink();
                if (url) {
                  setInstaVideoUrl(String(url));
                  setInstaVideoOpen(true);
                } else {
                  setInstaTermsOpen(true);
                }
              } catch {
                setInstaTermsOpen(true);
              }
            }}>
              <img src={calendarIcon} alt="calendar"/>
              <span>Activate Availability Scheduling</span>
              <img src={infoIcon} alt="info"/>
            </button>
          </div>
        )}

        {/* Calendar Section */}
        <section className={styles.calendarSection}>
          <div className={styles.calendarContainer}>
            <MonthView
              key={dataVersion}
              currentDate={currentDate}
              onDateClick={handleDateClick}
              onMonthChange={handleMonthChange}
              dateJobsMap={dateMarkerMap as any}
              getDisplayInfo={getDisplayInfo as any}
            />
            
            {/* Legend */}
            <div className={styles.legendSection}>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.booked}`}></span>
                Booked
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.offer}`}></span>
                Offer
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.available}`}></span>
                Available Shifts
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.completed}`}></span>
                Completed
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.expired}`}></span>
                Expired
              </div>
            </div>
          </div>
        </section>

      </main>
      
      {/* Mobile Add/Record a Shift */}
      <section className={styles.actionArea}>
        <div className={styles.actionsRow}>
          <button className={styles.primaryButton} onClick={handleOpenManualShift}>
            + Add/Record a Shift
          </button>
          <img
            src={infoIcon}
            alt="info"
            className={styles.infoIcon}
            onClick={handleOpenManualShiftInfo}
          />
        </div>
      </section>

      {/* React replacement for legacy selection modal */}
      <HygienistJobSelectionModal
        isOpen={!!selectionModal?.open}
        offers={(selectionModal?.offers || []) as any}
        dateKey={selectionModal?.dateKey || ''}
        hasAvailable={!!selectionModal?.hasAvailable}
        availableCount={selectionModal?.availableCount || 0}
        availableShifts={selectionModal?.availableShifts || []}
        canShowInstabook={!!selectionModal?.canShowInstabook}
        pendingAutobookJobId={selectionModal?.pendingAutobookJobId ?? null}
        bookedJobId={selectionModal?.bookedJobId ?? null}
        onSelectOffer={handleSelectOffer}
        onSelectAvailableJob={handleSelectAvailableJob}
        onViewMore={handleViewMoreShifts}
        onInstaBook={handleInstaBook}
        onViewBookedShift={() => {
          const id = selectionModal?.bookedJobId;
          setSelectionModal(null);
          if (id) {
            history.push({ pathname: 'confirmedOffer', state: { jobId: id, fromHome: true } });
          }
        }}
        onPendingAutobook={() => {
          const id = selectionModal?.pendingAutobookJobId;
          const dk = selectionModal?.dateKey || '';
          setSelectionModal(null);
          if (id) {
            const j = (getJobsByDate?.(dk) || []).find((x: any) => Number(x.id) === Number(id));
            history.push({
              pathname: 'hygienistInstabookConfirmation',
              state: { jobId: id, job: j || { id } },
            });
          }
        }}
        onClose={handleCloseSelectionModal}
      />

      {/* React Insta-Book Options Modal (top-level states) */}
      <InstaBookOptionsModal
        isOpen={!!instaOptions?.open}
        dateKey={instaOptions?.dateKey || ''}
        marked={!!instaOptions?.marked}
        canChangeHours={!!hygienist?.GlobalSettings?.maxDistance && isWithinTalentAutobookWindow(instaOptions?.dateKey || '')}
        onChangeHours={() => {
          if (!isWithinTalentAutobookWindow(instaOptions?.dateKey || '')) {
            setInstaOptions(null);
            setAutobookWindowError(true);
            return;
          }
          const currentSettings = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist?.GlobalSettings;
          if (!currentSettings?.maxDistance) {
            setInstaOptions(null);
            setNoSettingsPromptOpen(true);
            return;
          }
          // Close the options modal and open hours picker
          setInstaOptions(null)
          setHoursPicker(() => {
            const hyg = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
            const gs = hyg?.GlobalSettings;
            return {
              open: true,
              dateKey: instaOptions?.dateKey || '',
              marked: !!instaOptions?.marked,
              globalSettingsPreview: gs
                ? {
                    maxDistance: gs.maxDistance,
                    minShiftLength: gs.minShiftLength,
                    hourlyRate: gs.hourlyRate,
                  }
                : null,
            };
          })
        }}
        onRemoveDate={async () => {
          try {
            await Api.removeInstabookDate({ startDate: instaOptions?.dateKey || '' })
            try { (window as any)?.TempStars?.Analytics?.track?.('Autobook_Hours_Removed', { date: instaOptions?.dateKey }) } catch {}
            try {
              const refreshed = await Api.getUser();
              persistUserAccountSnapshot(removeDailySettingFromSnapshot(refreshed, instaOptions?.dateKey || ''));
            } catch {}
            await refreshAll();
          } catch {}
          // Close modal and stay on calendar
          setInstaOptions(null)
        }}
        onClose={() => {
          // Just close the modal, stay on calendar
          setInstaOptions(null)
        }}
      />
      {/* React replacements for legacy F7 modals */}
      <InstaVideoModal
        open={instaVideoOpen}
        url={instaVideoUrl}
        onClose={() => setInstaVideoOpen(false)}
        onContinue={() => { setInstaVideoOpen(false); setInstaTermsOpen(true); }}
      />
      <InstaTermsModal
        open={instaTermsOpen}
        checks={agreementChecks}
        setChecks={setAgreementChecks}
        onClose={() => setInstaTermsOpen(false)}
        onAgree={async () => {
          try {
            await Api.updateHygienist({ InstabookActivated: 1 });
            setInstaTermsOpen(false);
            setInstaSuccessOpen(true);
          } catch {}
        }}
      />
      <InstaSuccessModal
        open={instaSuccessOpen}
        name={(window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist?.firstName}
        onClose={() => setInstaSuccessOpen(false)}
        onGoSettings={() => {
          setInstaSuccessOpen(false);
          try { (window as any).TempStars?.App?.goReact?.('autoBook') } catch {}
        }}
      />
      <EmptyDayModal
        open={!!emptyDayModal?.open}
        canShowInsta={!!emptyDayModal?.canShowInstabook}
        onClose={() => setEmptyDayModal(null)}
        onSetEditInsta={() => {
          const hyg = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
          const hasGlobal = !!hyg?.GlobalSettings?.maxDistance;
          const dateKey = emptyDayModal?.dateKey || ''
          const daily = (hyg?.DailySettings || []) as Array<{ startDate: string }>
          // T2-5797: see comment above; normalize ISO startDate to YYYY-MM-DD.
          const isMarked = isDateKeyMarked(daily, dateKey);
          if (!isMarked && !isWithinTalentAutobookWindow(dateKey)) {
            setAutobookWindowError(true);
          } else if (hasGlobal || isMarked) {
            setInstaOptions({ open: true, dateKey, marked: isMarked })
          } else {
            setNoSettingsPromptOpen(true)
          }
          setEmptyDayModal(null)
        }}
      />
      {/* Available Today React modals */}
      <AvailableNowInfoModal
        open={availableUi.infoOpen}
        onClose={(dontShow) => { try { if (dontShow) safeLocalStorage.setItem('availableNoShow', 'true'); } catch {}; availableUi.setInfoOpen(false) }}
      />
      <AvailableNowPenaltyModal
        open={availableUi.penaltyOpen}
        daysRemaining={blockedDaysRemaining}
        onClose={() => availableUi.setPenaltyOpen(false)}
      />
      <ShiftsTodayPromptModal
        open={availableUi.promptOpen}
        dateKey={availableUi.promptDateKey}
        onYes={() => { availableUi.setPromptOpen(false); history.push({ pathname: 'availableJobs', state: { date: availableUi.promptDateKey, isAvailable: true, withDate: true } }); }}
        onNo={() => availableUi.setPromptOpen(false)}
      />
      <ManualShiftCongratsModal
        open={manualShiftCongratsOpen}
        onCancel={handleCloseManualShiftCongrats}
        onContinue={handleContinueManualShift}
      />
      <ManualShiftInfoModal
        open={manualShiftInfoOpen}
        onClose={handleCloseManualShiftInfo}
      />
      <HoursPickerModal
        isOpen={!!hoursPicker?.open}
        dateKey={hoursPicker?.dateKey || ''}
        marked={!!hoursPicker?.marked}
        globalSettingsPreview={hoursPicker?.globalSettingsPreview ?? null}
        onConfirm={async ({ start, end }) => {
          // A picker can remain open across local midnight. Re-check before a write.
          if (!isWithinTalentAutobookWindow(hoursPicker?.dateKey || '')) {
            setHoursPicker(null);
            setInstaOptions(null);
            setAutobookWindowError(true);
            return;
          }
          try {
            const dateStr = hoursPicker?.dateKey || ''
            // Format to server format: YYYY-MM-DD HH:mm:ss
            const s = dayjs(`${dateStr} ${start}`, 'YYYY-MM-DD h:mm A').format('YYYY-MM-DD HH:mm:ss')
            const e = dayjs(`${dateStr} ${end}`, 'YYYY-MM-DD h:mm A').format('YYYY-MM-DD HH:mm:ss')
            if (hoursPicker?.marked) {
              await Api.updateInstabookDate({ startDate: dateStr, start: s, end: e })
            } else {
              await Api.setInstabookSettings({ startDate: dateStr, start: s, end: e })
            }
            try { (window as any)?.TempStars?.Analytics?.track?.('Autobook_Hours_Set', { date: dateStr, start_time: s, end_time: e }) } catch {}
            try {
              const refreshed = await Api.getUser();
              /**
               * T2-5797: the purple Auto-book dot is rendered by
               * `applyPurpleBadgesFromLocalUser`, which reads
               * `userAccount.hygienist.DailySettings` from localStorage. On
               * dates that *also* have ≥1 available shift, the post-refresh
               * effect re-applies all badges and `applyAvailabilityBadges`
               * has just cleared the purple class — so we depend entirely on
               * the snapshot we persist here. If the GET response races the
               * preceding PUT (or in-flight dedupe returns an older response)
               * the new row is missing and the dot never re-appears. Patch
               * the just-saved row into the snapshot so the badge restorer
               * always sees it.
               */
              const snapshot: any = refreshed || {};
              if (snapshot?.hygienist) {
                const existing: Array<any> = Array.isArray(snapshot.hygienist.DailySettings)
                  ? snapshot.hygienist.DailySettings
                  : [];
                const alreadyPresent = existing.some((d: any) => {
                  const sd = typeof d?.startDate === 'string' ? d.startDate : '';
                  return sd.slice(0, 10) === dateStr;
                });
                if (!alreadyPresent) {
                  snapshot.hygienist.DailySettings = [
                    ...existing,
                    { startDate: dateStr, start: s, end: e },
                  ];
                }
              }
              persistUserAccountSnapshot(snapshot);
            } catch {}
            await refreshAll();
          } catch {}
          // Close modal and stay on calendar
          setHoursPicker(null)
          setInstaOptions(null)
        }}
        onRemove={async () => {
          try {
            const dateStr = hoursPicker?.dateKey || ''
            await Api.removeInstabookDate({ startDate: dateStr })
            try { (window as any)?.TempStars?.Analytics?.track?.('Autobook_Hours_Removed', { date: dateStr }) } catch {}
            try {
              const refreshed = await Api.getUser();
              persistUserAccountSnapshot(removeDailySettingFromSnapshot(refreshed, dateStr));
            } catch {}
            await refreshAll();
          } catch {}
          // Close modal and stay on calendar
          setHoursPicker(null)
          setInstaOptions(null)
        }}
        onClose={() => {
          // Just close the modal without any async operations
          setHoursPicker(null)
        }}
      />
      <BrandedAlertModal
        isOpen={autobookWindowError}
        onClose={() => setAutobookWindowError(false)}
        headerLabel="Auto Book"
        title="Choose a date in the coming two weeks"
        actions={[{ label: 'OK', variant: 'primary', onClick: () => setAutobookWindowError(false) }]}
      >
        {AUTOBOOK_WINDOW_MESSAGE}
      </BrandedAlertModal>
      {/* React prompt when no global settings and flag ON */}
      <SimpleModal isOpen={noSettingsPromptOpen} onClose={() => { setNoSettingsPromptOpen(false); (window as any).TempStars?.App?.goReact?.('autoBook') }}>
        <div style={{ padding: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Availability settings required</h3>
          <div style={{ color: 'var(--text-secondary, #666)', marginBottom: '0.75rem' }}>
            To set Auto-book availability on your calendar, please configure your global settings first.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="bigButtonInNewStyle" onClick={() => { setNoSettingsPromptOpen(false); (window as any).TempStars?.App?.goReact?.('autoBook') }}>Go to availability settings</button>
          </div>
        </div>
      </SimpleModal>
      {/* T2-6044: Legacy promotion-survey CTA removed (writes to Survey/SurveyAnswers). */}

      {/* Announcement */}
      <PhotoAnnounceModal
        open={!!announcement?.open}
        photoUrl={announcement?.photoUrl}
        linkUrl={announcement?.linkUrl}
        role="hygienist"
        onDismiss={() => setAnnouncement(null)}
      />

      {/* T2-5838: Work Experience prompt uses BrandedAlertModal to match the
          Figma pop-up styling (shared modal shell introduced in T2-5767). */}
      <BrandedAlertModal
        isOpen={workExpOpen}
        onClose={() => setWorkExpOpen(false)}
        headerLabel="Profile Update"
        title={`Heads up, ${(window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist?.firstName || ''}!`}
        actions={[
          { label: 'Fill Out Work Experience', variant: 'primary', onClick: () => { setWorkExpOpen(false); (window as any).TempStars?.App?.goReact('hygienistProfile', { isOpenPositionModal: true }); } },
          { label: "I'll do this later", variant: 'secondary', onClick: () => setWorkExpOpen(false) }
        ]}
      >
        Offices need to see Work Experience in offers. It looks like you haven't added this yet.
      </BrandedAlertModal>

      {/* T2-5838: Add Languages prompt uses BrandedAlertModal. */}
      <BrandedAlertModal
        isOpen={languagesOpen}
        onClose={() => setLanguagesOpen(false)}
        headerLabel="Profile Update"
        title="Add Languages to Your Profile"
        actions={[
          { label: 'Add Languages', variant: 'primary', onClick: () => { setLanguagesOpen(false); (window as any).TempStars?.App?.goReact('hygienistProfile', { isOpenQualificationModal: true, scrollToLanguages: true }); } },
          { label: 'Dismiss', variant: 'secondary', onClick: async () => { setLanguagesOpen(false); try { await Api.dismissLanguagesModal(); } catch {} } }
        ]}
      >
        Offices love to know which languages you speak – it often helps them choose the best match for their patients.
      </BrandedAlertModal>

      {/* T2-6634: Required education credentials confirmation (server-backed). */}
      <EducationCredentialsConfirmDialog
        open={gradYearOpen}
        hygienist={hygienist}
        onSaved={() => setGradYearOpen(false)}
      />

      {/* Profile Update prompt removed - superseded by forced qualification flow on login */}
      {/* <ConfirmDialog
        open={false}
        onClose={() => {}}
        title={'Update Your Profile'}
        content={(<div>
          Great news! We’re shifting to an improved profile system that enhances how your offers are presented to dental offices, while still preserving your ability to shine as an individual.
          <br/>This means we’ll be doing away with resumes soon.
          <br/>The official change is scheduled for {(profileUpdateOpen?.formatted) || ''}. Complete your enhanced profile now so you can continue to send offers after {dayjs(profileUpdateOpen?.raw).format('MMM Do')}.
        </div>)}
        actions={[
          { label: 'Update Now', variant: 'contained', onClick: () => { setProfileUpdateOpen(null); const hygId = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist?.id; (window as any).TempStars?.App?.goReact('hygienistProfile'); safeLocalStorage.setItem('isProfileUpdated', String(hygId)); safeLocalStorage.setItem('skillsModalSeen', 'true'); } },
          { label: 'Later', onClick: () => {} }
        ]}
      /> */}

      {/* T2-5838: Invoice prompt uses BrandedAlertModal. */}
      {/* T2-6019: "I'll Do It Later" advances the queue / closes the modal
          OPTIMISTICALLY (before awaiting the API). The previous flow awaited
          `Api.createInvoiceLater` inside the click handler, which left the
          modal visually open until the round-trip resolved and could leave
          the talent "stuck" if the request was slow or transiently failed.
          The window-scoped `invoiceStatus[job.id]` flag is set synchronously
          here so the parent useEffect won't re-queue the same job on its
          next run, even if the API call ends up failing. */}
      <BrandedAlertModal
        isOpen={invoiceOpen && invoiceIdx < invoiceQueue.length}
        onClose={() => { setInvoiceOpen(false); setInvoiceQueue([]); setInvoiceIdx(0); }}
        headerLabel="Pending Invoice"
        title="Create and Send Invoice for this shift?"
        actions={[
          { label: 'Yes', variant: 'primary', onClick: () => { const job = invoiceQueue[invoiceIdx]; const currentHygienist = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist; const jobWithHygienist = { ...job, hygienist: job.hygienist || currentHygienist }; try { (window as any).TempStars.Hygienist.invoiceStatus[job.id] = true; (window as any).TempStars.Hygienist.invoiceJobId = job.id; } catch {} setInvoiceOpen(false); history.push({ pathname: 'hygienistInvoice', state: { job: jobWithHygienist, isViewInvoice: false } }); } },
          { label: "I'll Do It Later", variant: 'secondary', onClick: () => { const job = invoiceQueue[invoiceIdx]; if (!job) return; try { (window as any).TempStars.Hygienist.invoiceStatus[job.id] = true; } catch {} const next = invoiceIdx + 1; if (next < invoiceQueue.length) { setInvoiceIdx(next); } else { setInvoiceOpen(false); setInvoiceQueue([]); setInvoiceIdx(0); } Promise.resolve(Api.createInvoiceLater(job.id)).catch(() => { /* fire-and-forget; flag above protects against re-prompt */ }); } }
        ]}
      >
        {(() => { const job = invoiceQueue[invoiceIdx]; if (!job) return null; return `${job?.location?.practiceName || ''} — ${dayjs(job?.shifts?.[0]?.shiftDate).format('MMM D')}, ${job?.shifts?.[0]?.postedStart} - ${job?.shifts?.[0]?.postedEnd}`; })()}
      </BrandedAlertModal>

      {/* Job Board dialogs */}
      <ConfirmDialog
        open={jobBoardBlockedOpen}
        onClose={() => setJobBoardBlockedOpen(false)}
        title={<b>Penalty Block in Effect</b> as any}
        content={(<div>While your penalty is in effect, you can view any job postings that you have already applied for. However, you won't be able to see any new postings until your penalty block is over.</div>)}
        actions={[{ label: 'OK', variant: 'contained', onClick: async () => { setJobBoardBlockedOpen(false); (window as any).TempStars?.App?.goReact('jobPostsList'); } }]}
      />
      <ConfirmDialog
        open={jobBoardHelpOpen}
        onClose={() => setJobBoardHelpOpen(false)}
        title={<b>Important</b> as any}
        content={(<div>This Job Board is only for finding and applying for permanent and contract positions, ie. getting hired by an office. <b>It's not for temping shifts.</b><br/><br/>To find temping shifts, tap "View All Shifts" above the calendar, or tap a green square on the calendar to view temping shifts for that date.</div>)}
        actions={[{ label: 'I Understand', variant: 'contained', onClick: async () => { setJobBoardHelpOpen(false); try { await Api.updateHygienist({ isJobBoardHelpMsgSeen: 1 }); } catch {} (window as any).TempStars?.App?.goReact('jobPostsList'); } }]}
      />
      
      <NoShiftsModal
        open={noShiftsModalOpen}
        userCity={userCity}
        onClose={() => setNoShiftsModalOpen(false)}
      />

      <MembershipRestrictionModal
        isOpen={!!membershipDialog}
        copy={membershipDialog}
        onClose={() => setMembershipDialog(null)}
      />
      
      <FixedLoader loading={isLoading || isToggleLoading} />
    </div>
    </>
  );
};

export default HygienistHome;
